import React, { useEffect, useState } from "react";
import jsPDF from "jspdf";
import {
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
} from "reactstrap";
import { CSVLink } from "react-csv";
import {
  Block,
  Icon,
  Row,
  Col,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
} from "../../components/Component";

import moment from "moment";
import { formatCurrency } from "../../utils";
import Logo from "../../assets/images/logo.png";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "jspdf-autotable";

const ViewStationCompareModal = ({
  modalForm,
  setModalForm,
  ViewStationCompareData,
  metaDataComparisonState,
}) => {
  const toggleForm = () => setModalForm(!modalForm);
  const [data, setData] = useState(
    [
      ...ViewStationCompareData?.filter(
        (item) => item?.pricing_station !== "-"
      ),
      ...ViewStationCompareData?.filter(
        (item) => item?.pricing_station === "-"
      ),
    ] ?? []
  );

  const [exporting, setExporting] = useState(false);
  const [onSearch, setonSearch] = useState(true);
  const [onSearchText, setSearchText] = useState("");
  // const [actionText, setActionText] = useState("");
  // const [currentPage, setCurrentPage] = useState(1);
  // const [itemPerPage, setItemPerPage] = useState(9);
  const [sort, setSortState] = useState("asc");
  const exportToPDF = () => {
    setExporting(true);
    try {
      const doc = new jsPDF("landscape");

      const imgProps = doc.getImageProperties(Logo);
      const imgWidth = 12; //which is your container width
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

      // Set title
      const title = "Saving Report";
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const textWidth =
        (doc.getStringUnitWidth(title) * doc.internal.getFontSize()) /
        doc.internal.scaleFactor;
      const textX = (pageWidth - textWidth) / 2;

      doc.setFontSize(16);
      doc.text(title, textX, 20);

      // Add the three columns
      const col1 = `Your Total Cost: ${formatCurrency(
        metaDataComparisonState?.total_saving_tsc_1,
        2
      )}`;
      const col2 = `Total Cost with Us: ${formatCurrency(
        metaDataComparisonState?.total_saving_tsc_2,
        2
      )}`;
      const totalSavingValueInHeader =
        +metaDataComparisonState?.total_saving_tsc_1 -
        +metaDataComparisonState?.total_saving_tsc_2;
      const col3 = `Total Savings: ${formatCurrency(
        totalSavingValueInHeader,
        2
      )}`;

      const col1X = 20;
      const col2X = pageWidth / 2;
      const col3X = pageWidth - 20;

      doc.setFontSize(10);
      doc.text(col1, col1X, 30, { align: "left" });
      doc.text(col2, col2X, 30, { align: "center" });
      if (+totalSavingValueInHeader < 0) {
        doc.setTextColor(255, 0, 0); // Set text color to red
      } else {
        doc.setTextColor(0, 128, 0); // Set text color to green
      }
      doc.text(col3, col3X, 30, { align: "right" });
      doc.setTextColor(0, 0, 0); // Reset text color to black

      // Prepare table data for autoTable
      const tableData = data
        .filter((item) => item.pricing_station !== "-")
        .map((item) => {
          // const savingAmount = parseFloat(
          //   String(data?.cell?.raw).replace(/[$,]/g, "")
          // );

          return [
            moment(item.transaction_date).format("MM/DD/YYYY"),
            item.report_fuel_qty,
            item.report_station,
            `${item.report_city}, ${item.report_state}`,
            `${formatCurrency(item.report_retail_price)}`,
            `${formatCurrency(item.report_fuel_price)}`,
            `${formatCurrency(item.report_discount_per_gallon)}`,
            `${formatCurrency(item.tsc_price_1)}`,
            item.pricing_station,
            `${item.pricing_city !== "-"
              ? `${item.pricing_city}, ${item.pricing_state}`
              : "-"
            }`,
            `${formatCurrency(item.price_retail_price)}`,
            `${formatCurrency(item.price_fuel_price)}`,
            `${formatCurrency(item.price_discount_per_gallon)}`,
            `${formatCurrency(item.tsc_price_2)}`,
            `${item.totalSavingAmount}`,
          ];
        });

      // Table headers
      const headers = [
        "Date",
        "Qty",
        "Report Station",
        "City, State",
        "Your Retail Price",
        "Your Price",
        "Your Discount Per Gallon",
        "Transaction Price",
        "Pricing Station",
        "City, State",
        "RoadEx Retail Price",
        "RoadEx Price",
        "RoadEx Discount Per Gallon",
        "Transaction Price",
        "Total Saving",
      ];

      // Add table to PDF without background color and row theme
      if (tableData?.length) {
        doc.autoTable({
          head: [headers],
          body: tableData,
          startY: 40, // Adjusted to add gap between columns and table
          headStyles: { fillColor: [49, 79, 160] },
          styles: { fontSize: 5 }, // Decreased font size
          margin: { top: 30 },
          didParseCell: function (data) {
            if (
              [1, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14]?.includes(
                data.column.index
              )
            ) {
              if (data.row.section === "body" && data.column.index === 14) {
                const savingAmount = parseFloat(data?.cell?.raw);

                if (savingAmount < 0) {
                  data.cell.styles.textColor = [255, 0, 0]; // Red for negative values
                }
                data.cell.text = [formatCurrency(savingAmount)];
              }
            }
          },
        });
      }

      // Add unmatched states table
      const unmatchedData = data
        .filter((item) => item.pricing_station === "-")
        .map((item) => [
          moment(item.transaction_date).format("MM/DD/YYYY"),
          item.report_fuel_qty,
          item.report_station,
          `${item.report_city}, ${item.report_state}`,
          `${formatCurrency(item.report_retail_price)}`,
          `${formatCurrency(item.report_fuel_price)}`,
          `${formatCurrency(item.report_discount_per_gallon)}`,
          `${formatCurrency(item.tsc_price_1)}`,
          item.pricing_station,
          `${item.pricing_city !== "-"
            ? `${item.pricing_city}, ${item.pricing_state}`
            : "-"
          }`,
          `${formatCurrency(item.price_retail_price)}`,
          `${formatCurrency(item.price_fuel_price)}`,
          `${formatCurrency(item.price_discount_per_gallon)}`,
          `${formatCurrency(item.tsc_price_2)}`,
          `${formatCurrency(item.totalSavingAmount)}`,
        ]);

      const unmatchedHeaders = [
        "Date",
        "Qty",
        "Report Station",
        "City, State",
        "Your Retail Price",
        "Your Price",
        "Your Discount Per Gallon",
        "Transaction Price",
        "Pricing Station",
        "City, State",
        "RoadEx Retail Price",
        "RoadEx Price",
        "RoadEx Discount Per Gallon",
        "Transaction Price",
        "Total Saving",
      ];

      if (unmatchedData.length > 0) {
        doc.addPage();

        doc.setFontSize(16);
        doc.text("Not Matched Stations", textX, 20);

        doc.autoTable({
          head: [unmatchedHeaders],
          body: unmatchedData,
          startY: 30,
          headStyles: { fillColor: [49, 79, 160] },
          styles: { fontSize: 5 },
          margin: { top: 30 },
          didParseCell: function (data) {
            // if (
            //   [1, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14]?.includes(
            //     data.column.index
            //   )
            // ) {
            // 	// if (data.section.body ==="body" && data.column.index === 14) {
            //   //   const savingAmount = parseFloat(data?.cell?.raw);
            //   //   data.cell.text = [formatCurrency(savingAmount)];
            //   // }
            // }
          },
        });
      }

      doc.setFontSize(8);
      doc.setTextColor(10, 10, 10);

      const totalPages = doc.getNumberOfPages();
      for (let pageNo = 1; pageNo <= totalPages; pageNo++) {
        doc.setPage(pageNo);
        doc.addImage(Logo, "PNG", 20, 10, imgWidth, imgHeight);
        doc.text(
          "PAGE " + pageNo + " OF " + totalPages,
          pageWidth - 20,
          pageHeight - 10,
          {
            align: "right",
          }
        );
      }

      // Save the generated PDF
      doc.save("station_comparison_data.pdf");
    } catch (error) {
      console.error(error);

      toast.error(error || "Error while exporting data, please try again!", {
        position: "top-center",
      });
    } finally {
      setExporting(false);
    }
  };

  const headers = [
    { label: "Transaction Date", key: "transaction_date" },
    { label: "Fuel Quantity", key: "report_fuel_qty" },
    { label: "Report Station", key: "report_station" },
    { label: "City", key: "report_city" },
    { label: "State", key: "report_state" },
    { label: "Your Retail Price", key: "report_retail_price" },
    { label: "Your Price", key: "report_fuel_price" },
    { label: "Your Discount Per Gallon", key: "report_discount_per_gallon" },
    { label: "Transaction Price", key: "tsc_price_1" },
    { label: "Pricing Station", key: "pricing_station" },
    { label: "City", key: "pricing_city" },
    { label: "State", key: "pricing_state" },
    { label: "RoadEx Retail Price", key: "price_retail_price" },
    { label: "RoadEx Price", key: "price_fuel_price" },
    { label: "RoadEx Discount Per Gallon", key: "price_discount_per_gallon" },
    { label: "Transaction Price", key: "tsc_price_2" },
    { label: "Total Saving Amount", key: "totalSavingAmount" },
  ];

  // Sorting data
  const sortFunc = (params) => {
    let defaultData = data;
    if (params === "asc") {
      let sortedData = defaultData.sort((a, b) =>
        a.transaction_date.localeCompare(b.transaction_date)
      );
      setData([
        ...sortedData?.filter((item) => item?.pricing_station !== "-"),
        ...sortedData?.filter((item) => item?.pricing_station === "-"),
      ]);
    } else if (params === "dsc") {
      let sortedData = defaultData.sort((a, b) =>
        b.transaction_date.localeCompare(a.transaction_date)
      );
      setData([
        ...sortedData?.filter((item) => item?.pricing_station !== "-"),
        ...sortedData?.filter((item) => item?.pricing_station === "-"),
      ]);
    }
  };

  // Changing state value when searching name
  useEffect(() => {
    if (onSearchText !== "") {
      const filteredObject = [
        ...ViewStationCompareData?.filter(
          (item) => item?.pricing_station !== "-"
        ),
        ...ViewStationCompareData?.filter(
          (item) => item?.pricing_station === "-"
        ),
      ]?.filter((item) => {
        return (
          item?.invoice_number.pricing_station?.includes(
            onSearchText?.toLowerCase()
          ) ||
          item?.pricing_station
            ?.toLowerCase()
            ?.includes(onSearchText?.toLowerCase()) ||
          item?.report_station
            ?.toLowerCase()
            ?.includes(onSearchText?.toLowerCase())
        );
      });
      setData([...filteredObject]);
    } else {
      setData([
        ...ViewStationCompareData?.filter(
          (item) => item?.pricing_station !== "-"
        ),
        ...ViewStationCompareData?.filter(
          (item) => item?.pricing_station === "-"
        ),
      ]);
    }
  }, [onSearchText, setData]);

  useEffect(() => {
    setData([
      ...ViewStationCompareData?.filter(
        (item) => item?.pricing_station !== "-"
      ),
      ...ViewStationCompareData?.filter(
        (item) => item?.pricing_station === "-"
      ),
    ]);
  }, [ViewStationCompareData]);

  // onChange function for searching name
  const onFilterChange = (e) => {
    setSearchText(e.target.value);
  };

  // function to toggle the search option
  const toggle = () => setonSearch(!onSearch);

  // Change Page
  // const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <Modal isOpen={modalForm} className="custom-modal-width">
      <ModalHeader
        toggle={toggleForm}
        close={
          <div className="d-flex justify-content-end">
            <CSVLink
              className="btn btn-primary"
              filename={"station-comparison.csv"}
              data={[
                ...data?.filter((item) => item?.pricing_station !== "-"),
                ...data?.filter((item) => item?.pricing_station === "-"),
              ]?.map((item) => {
                return {
                  transaction_date: moment(item.transaction_date).format(
                    "MM/DD/YYYY"
                  ),
                  report_fuel_qty: item.report_fuel_qty,
                  report_station: item.report_station,
                  report_city: item.report_city,
                  report_state: item.report_state,
                  report_retail_price: (+item?.report_retail_price)?.toFixed(2),
                  report_fuel_price: (+item?.report_fuel_price)?.toFixed(2),
                  report_discount_per_gallon:
                    (+item?.report_discount_per_gallon)?.toFixed(2),
                  tsc_price_1: (+item?.tsc_price_1)?.toFixed(2),
                  pricing_station: item?.pricing_station,
                  pricing_city: item?.pricing_city,
                  pricing_state: item?.pricing_state,
                  price_retail_price: (+item?.price_retail_price)?.toFixed(2),
                  price_fuel_price: (+item?.price_fuel_price)?.toFixed(2),
                  price_discount_per_gallon:
                    (+item?.price_discount_per_gallon)?.toFixed(2),
                  tsc_price_2: (+item?.tsc_price_2)?.toFixed(2),
                  totalSavingAmount: (+item?.totalSavingAmount)?.toFixed(2),
                };
              })}
              headers={headers}
            >
              <span className=" d-none d-md-block">Export&nbsp;</span>
              <span>CSV</span>
            </CSVLink>
            <Button
              className="btn btn-primary "
              onClick={() => [
                setExporting(true),
                setTimeout(exportToPDF, 0)
              ]}
              disabled={exporting}
              style={{ marginLeft: "10px" }}
            >
              {exporting ? (
                <Spinner size="sm" />
              ) : (
                <>
                  <span className=" d-none d-md-block">Export&nbsp;</span>
                  <span>PDF</span>
                </>
              )}
            </Button>
          </div>
        }
      >
        View Station Comparison
      </ModalHeader>
      <ModalBody>
        <Block>
          <DataTable className="card-stretch">
            <div
              className="card-inner position-relative card-tools-toggle"
              style={{ padding: "1rem" }}
            >
              <div className="card-title-group">
                <div className="card-tools">
                  <div className="form-inline flex-nowrap gx-3">
                    <div className="btn-wrap">
                      <span className="d-md-none">
                        <Button
                          color="light"
                          outline
                          // disabled={actionText !== "" ? false : true}
                          className="btn-dim btn-icon"
                          onClick={() => {
                            toggleForm();
                            // setActionText("");
                            setSortState("asc");
                            setSearchText("");
                            // setItemPerPage(9);
                          }}
                        >
                          <Icon name="arrow-left"></Icon>
                        </Button>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="card-tools me-n1">
                  <ul className="btn-toolbar gx-1">
                    <li>
                      <a
                        href="#search"
                        onClick={(ev) => {
                          ev.preventDefault();
                          toggle();
                        }}
                        className="btn btn-icon search-toggle toggle-search"
                      >
                        <Icon name="search"></Icon>
                      </a>
                    </li>
                    <li>
                      <div className="toggle-wrap">
                        <UncontrolledDropdown>
                          <DropdownToggle
                            color="tranparent"
                            className="btn btn-trigger btn-icon dropdown-toggle"
                          >
                            <Icon name="setting"></Icon>
                          </DropdownToggle>
                          <DropdownMenu end className="dropdown-menu-xs">
                            {/* <ul className="link-check">
                              <li>
                                <span>Show</span>
                              </li>
                              <li
                                className={itemPerPage === 10 ? "active" : ""}
                              >
                                <DropdownItem
                                  tag="a"
                                  href="#dropdownitem"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    setItemPerPage(10);
                                  }}
                                >
                                  10
                                </DropdownItem>
                              </li>
                              <li
                                className={itemPerPage === 15 ? "active" : ""}
                              >
                                <DropdownItem
                                  tag="a"
                                  href="#dropdownitem"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    setItemPerPage(15);
                                  }}
                                >
                                  15
                                </DropdownItem>
                              </li>
                            </ul> */}
                            <ul className="link-check">
                              <li>
                                <span>Order</span>
                              </li>
                              <li className={sort === "dsc" ? "active" : ""}>
                                <DropdownItem
                                  tag="a"
                                  href="#dropdownitem"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    setSortState("dsc");
                                    sortFunc("dsc");
                                  }}
                                >
                                  DESC
                                </DropdownItem>
                              </li>
                              <li className={sort === "asc" ? "active" : ""}>
                                <DropdownItem
                                  tag="a"
                                  href="#dropdownitem"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    setSortState("asc");
                                    sortFunc("asc");
                                  }}
                                >
                                  ASC
                                </DropdownItem>
                              </li>
                            </ul>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
              <div
                className={`card-search search-wrap ${!onSearch && "active"}`}
              >
                <div className="card-body">
                  <div className="search-content">
                    <Button
                      className="search-back btn-icon toggle-search active"
                      onClick={() => {
                        setSearchText("");
                        toggle();
                      }}
                    >
                      <Icon name="arrow-left"></Icon>
                    </Button>
                    <input
                      type="text"
                      className="border-transparent form-focus-none form-control"
                      placeholder="Search by user or email"
                      value={onSearchText}
                      onChange={(e) => onFilterChange(e)}
                    />
                    <Button className="search-submit btn-icon">
                      <Icon name="search"></Icon>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <DataTableBody className="overflow-auto view-state">
              <DataTableHead>
                {/* <DataTableRow>
                  <span className="sub-text fw-bold">Invoice Number</span>
                </DataTableRow> */}
                <DataTableRow>
                  <span className="sub-text fw-bold">Transaction Date</span>
                </DataTableRow>
                <DataTableRow>
                  <span className="sub-text fw-bold">Report Station</span>
                </DataTableRow>
                <DataTableRow>
                  <span className="sub-text fw-bold">Pricing Station</span>
                </DataTableRow>
                <DataTableRow>
                  <span className="sub-text fw-bold">Fuel Quantity</span>
                </DataTableRow>
                <DataTableRow>
                  <span className="sub-text fw-bold">Report Fuel Price</span>
                </DataTableRow>
                <DataTableRow>
                  <span className="sub-text fw-bold">Pricing Fuel Price</span>
                </DataTableRow>
                <DataTableRow>
                  <span className="sub-text fw-bold">Total Saving Amount</span>
                </DataTableRow>
              </DataTableHead>
              {data?.length > 0
                ? data.map((item, index) => {
                  return (
                    <DataTableItem key={index}>
                      {/* <DataTableRow>
                          <span>{item?.invoice_number}</span>
                        </DataTableRow> */}
                      <DataTableRow>
                        <span>
                          {moment(item?.transaction_date).format(
                            "MM-DD-YYYY"
                          )}
                        </span>
                      </DataTableRow>
                      <DataTableRow>
                        <span>{item?.report_station}</span>
                      </DataTableRow>
                      <DataTableRow>
                        <span>{item?.pricing_station}</span>
                      </DataTableRow>
                      <DataTableRow>
                        <span>{item?.report_fuel_qty}</span>
                      </DataTableRow>
                      <DataTableRow>
                        <span>{formatCurrency(item?.report_fuel_price)}</span>
                      </DataTableRow>
                      <DataTableRow>
                        <span>{formatCurrency(item?.price_fuel_price)}</span>
                      </DataTableRow>

                      <DataTableRow>
                        <span
                          style={{
                            color:
                              item?.totalSavingAmount < 0 ? "red" : "#526484",
                          }}
                        >
                          {formatCurrency(item?.totalSavingAmount)}
                        </span>
                      </DataTableRow>
                    </DataTableItem>
                  );
                })
                : null}
            </DataTableBody>

            <div className="card-inner">
              {data.length > 0 ? (
                <></>
              ) : (
                <div className="text-center state-compare-modal d-flex justify-content-center align-items-center">
                  <span className="text-silent">No data found</span>
                </div>
              )}
            </div>
          </DataTable>
        </Block>
        <Row className="justify-content-end mx-auto">
          <Col className="col-auto px-0">
            <div className="form-group">
              <div className="d-flex justify-content-end pt-5">
                <Button
                  color="primary"
                  onClick={() => {
                    toggleForm();
                    // setActionText("");
                    setSearchText("");
                    setSortState("asc");
                    // setItemPerPage(9);
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
};

export default ViewStationCompareModal;
